import React from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const ProductCards = ({ productData, variant = 0 }) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  // Define layout settings based on variant
  let displayCount = 4;
  let gridStyle = {};

  if (variant === 0) {
    // Layout 0: 5 items in a clean row on desktop, scattered physical photos on mobile
    displayCount = 5;
    gridStyle = {
      display: isMobile ? "block" : "flex",
      position: isMobile ? "relative" : "static",
      height: isMobile ? "500px" : "auto", // Compact height for scattered photos
      width: "100%",
      flexWrap: "wrap",
      justifyContent: "center",
      gap: 2.5,
    };
  } else if (variant === 1) {
    // Layout 1: 4 items, staggered (masonry feel on both)
    displayCount = 4;
    gridStyle = {
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      gap: { xs: 1.5, sm: 3 },
    };
  } else {
    // Layout 2: 4 items in an elegant editorial row (Arch-shaped images)
    displayCount = 4;
    gridStyle = {
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      gap: { xs: 2, sm: 3.5 }, // Slightly wider, elegant gap
    };
  }

  // Container variants for staggered entrance
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: variant === 0 ? 0.15 : variant === 1 ? 0.12 : 0.18,
      }
    }
  };

  // Card entrance animations based on lookbook type
  const getCardVariants = (idx, scatterProps) => {
    if (variant === 0) {
      // Physical photograph: pop up with slight rotation (spring physics)
      const targetRotate = isMobile && scatterProps.rotate ? scatterProps.rotate : "0deg";
      const desktopRotate = idx % 2 === 0 ? "-2deg" : "2.5deg"; // alternating photo offsets on desktop
      const finalRotate = isMobile ? targetRotate : desktopRotate;
      return {
        hidden: {
          opacity: 0,
          scale: 0.6,
          y: 60,
          rotate: isMobile ? targetRotate : "0deg"
        },
        visible: {
          opacity: 1,
          scale: 1,
          y: 0,
          rotate: finalRotate,
          transition: {
            type: "spring",
            stiffness: 110,
            damping: 11
          }
        }
      };
    } else if (variant === 1) {
      // Staggered Masonry: Slide up and fade in smoothly
      return {
        hidden: {
          opacity: 0,
          y: 80
        },
        visible: {
          opacity: 1,
          y: 0,
          transition: {
            ease: "easeOut",
            duration: 0.7
          }
        }
      };
    } else {
      // Arch shaped editorial: Elegant scale up and fade (zoom reveal)
      return {
        hidden: {
          opacity: 0,
          scale: 0.82,
          y: 35
        },
        visible: {
          opacity: 1,
          scale: 1,
          y: 0,
          transition: {
            type: "spring",
            stiffness: 90,
            damping: 14
          }
        }
      };
    }
  };

  const itemsToDisplay = productData.slice(0, displayCount);

  return (
    <Box
      component={motion.div}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.6 }}
      variants={containerVariants}
      sx={gridStyle}
    >
      {itemsToDisplay.map((product, index) => {
        // Apply staggered effect for variant 1
        const isStaggered = variant === 1 && index % 2 !== 0;

        // Mobile scattered positions for variant 0
        const isMobileScattered = isMobile && variant === 0;
        const scatterPositions = [
          { top: "0px", left: "5%", rotate: "-6deg", zIndex: 1 },
          { top: "70px", left: "45%", rotate: "4deg", zIndex: 2 },
          { top: "150px", left: "10%", rotate: "-3deg", zIndex: 3 },
          { top: "230px", left: "50%", rotate: "5deg", zIndex: 4 },
          { top: "310px", left: "15%", rotate: "-4deg", zIndex: 5 },
        ];
        const scatterProps = isMobileScattered ? scatterPositions[index] : {};

        return (
          <Box
            component={motion.div}
            variants={getCardVariants(index, scatterProps)}
            key={index}
            onClick={() => {
              navigate("/product", { state: { product } });
            }}
            sx={{
              position: isMobileScattered ? "absolute" : "static",
              top: scatterProps.top || "auto",
              left: scatterProps.left || "auto",
              zIndex: scatterProps.zIndex || "auto",
              width: {
                xs: variant === 0 ? "130px" : "calc(50% - 8px)", // Compact sizes for tight pile
                sm: variant === 0 ? "210px" : variant === 1 ? "240px" : "220px"
              },
              display: "flex",
              flexDirection: "column",
              flexShrink: 0,
              cursor: "pointer",
              mt: { xs: isStaggered ? 3 : 0, sm: isStaggered ? 4 : 0 }, // Apply mobile stagger
              "&:hover .product-image-container": {
                boxShadow: "0 12px 24px rgba(0,0,0,0.1)",
              },
              "&:hover .product-image": {
                transform: "scale(1.05)",
              },
            }}
          >
            {/* Image Container */}
            <Box
              className="product-image-container"
              sx={{
                width: "100%",
                height: {
                  xs: variant === 0 ? "180px" : "240px",
                  sm: variant === 0 ? "280px" : variant === 1 ? "360px" : "330px"
                },
                borderRadius: variant === 2 ? "110px 110px 12px 12px" : (variant === 0 ? "4px" : "12px"),
                overflow: "hidden",
                backgroundColor: "#F9F9F9",
                border: variant === 2 ? "1px solid rgba(255, 107, 0, 0.15)" : (variant === 0 ? (isMobile ? "6px solid #FFF" : "10px solid #FFF") : "1px solid #EAEAEA"),
                boxShadow: variant === 0 ? "0 8px 24px rgba(0,0,0,0.12)" : "none",
                position: "relative",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                mb: variant === 0 ? 0 : 1.5,
              }}
            >
              <img
                className="product-image"
                src={product?.image_url}
                alt={product?.title || "Product"}
                onError={(e) => {
                  const fallbackImage = product?.images?.[0];
                  if (fallbackImage && !e.target.dataset.triedFallback) {
                    e.target.dataset.triedFallback = "true";
                    e.target.src = fallbackImage;
                  } else {
                    e.target.onerror = null;
                    e.target.src = require("../../assets/images/no_image.png");
                  }
                }}
                loading="lazy"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  transition: "transform 0.6s ease",
                }}
              />
            </Box>

            {/* Text has been completely removed to make the examples pure visual lookbooks */}
          </Box>
        );
      })}
    </Box>
  );
};

export default ProductCards;
